import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useModalConfirm } from "@ryde/redux";
import {
  ConfirmResult,
  currencySignSelector,
  dropdownAtomFamily,
  formatPrice,
  NO_VALUE_DISPLAY,
} from "@ryde/shared";

import { ColorIconButton } from "../../../controls";
import { IStationAdditionItemForm } from "./station-addition.models";
import { StationAdditionDisplayItemStyled } from "./station-addition.styled";

export const StationAdditionItemDisplay: FC<IStationAdditionItemForm> = ({
  removeHandler,
  index,
  onEdit,
  value,
}) => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const areaItems = useRecoilValue(dropdownAtomFamily("area"));
  const currencySign = useRecoilValue(currencySignSelector);

  const onArchiveClickEvent = useEvent(async (): Promise<void> => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t("general.ITEM_DELETE_MESSAGE"),
    });

    if (confirmationResult === ConfirmResult.OK) {
      removeHandler?.(index || 0);
    }
  });

  const areaItemNames: string = useMemo(() => {
    if (!value?.areaIds?.length) {
      return NO_VALUE_DISPLAY;
    }

    const items =
      value?.areaIds?.map(
        areaId =>
          areaItems.find(item => +item.value === +areaId)?.label ||
          NO_VALUE_DISPLAY,
      ) || [];
    return items?.join(",");
  }, [areaItems, value?.areaIds]);

  const startingFromStationIndex = useMemo(() => {
    if (!value?.startingFromStationIndex) {
      return NO_VALUE_DISPLAY;
    }

    return t("priceRate.SPECIFIC_AREA_FROM_STATION", {
      fromStationIndex: value?.startingFromStationIndex,
    });
  }, [value?.startingFromStationIndex, t]);

  return (
    <StationAdditionDisplayItemStyled>
      <p>{startingFromStationIndex}</p>
      <p>
        {currencySign} {formatPrice({ value: value?.price || 0 })}
      </p>
      <p className="break-word">{areaItemNames}</p>
      <div>
        <ColorIconButton iconType="EDIT" onClick={() => onEdit?.()} />
        <ColorIconButton
          iconType="DELETE"
          onClick={() => onArchiveClickEvent()}
        />
      </div>
    </StationAdditionDisplayItemStyled>
  );
};
